import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:7071/api';

class AdminService {
    constructor() {
        this.apiClient = axios.create({
            baseURL: API_BASE_URL,
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json'
            }
        });

        // Add request interceptor for authentication
        this.apiClient.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('authToken');
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // Add response interceptor for error handling
        this.apiClient.interceptors.response.use(
            (response) => response,
            (error) => {
                console.error('Admin API Error:', error);
                if (error.response?.status === 403) {
                    throw new Error('Access denied. Admin privileges required.');
                }
                if (error.response?.status === 401) {
                    throw new Error('Authentication required.');
                }
                throw error;
            }
        );
    }

    // Get advanced analytics with real data
    async getAdvancedAnalytics(timeRange = '30d', section = null) {
        try {
            console.log(`Fetching advanced analytics - timeRange: ${timeRange}, section: ${section || 'all'}`);
            
            const response = await this.apiClient.get('/analytics/get', {
                params: {
                    timeRange,
                    ...(section && { section })
                }
            });
            
            if (response.data && response.data.success) {
                return response.data;
            }
            
            throw new Error(response.data?.message || 'Failed to fetch advanced analytics');
        } catch (error) {
            console.error('Error fetching advanced analytics:', error);
            throw this.handleError(error);
        }
    }

    // Get video likes data
    async getVideoLikes(videoId = null, userId = null, limit = null) {
        try {
            console.log(`Fetching video likes - videoId: ${videoId}, userId: ${userId}, limit: ${limit}`);
            
            const response = await this.apiClient.get('/video/likes', {
                params: {
                    ...(videoId && { videoId }),
                    ...(userId && { userId }),
                    ...(limit && { limit })
                }
            });
            
            if (response.data && response.data.success) {
                return response.data;
            }
            
            throw new Error(response.data?.message || 'Failed to fetch video likes');
        } catch (error) {
            console.error('Error fetching video likes:', error);
            throw this.handleError(error);
        }
    }

    // Get advanced user activities
    async getAdvancedUserActivities(userId = null, activityType = null, timeRange = '30d', limit = 50) {
        try {
            console.log(`Fetching advanced user activities - userId: ${userId}, type: ${activityType}, timeRange: ${timeRange}`);
            
            const response = await this.apiClient.get('user-activity/advanced', {
                params: {
                    ...(userId && { userId }),
                    ...(activityType && { activityType }),
                    timeRange,
                    limit
                }
            });
            
            if (response.data && response.data.success) {
                return response.data;
            }
            
            throw new Error(response.data?.message || 'Failed to fetch user activities');
        } catch (error) {
            console.error('Error fetching user activities:', error);
            throw this.handleError(error);
        }
    }

    // Get video statistics
    async getVideoStats(videoId = null, metric = null, timeRange = '30d', limit = 50) {
        try {
            console.log(`Fetching video stats - videoId: ${videoId}, metric: ${metric}, timeRange: ${timeRange}`);
            
            const response = await this.apiClient.get('/video-stats', {
                params: {
                    ...(videoId && { videoId }),
                    ...(metric && { metric }),
                    timeRange,
                    limit
                }
            });
            
            if (response.data && response.data.success) {
                return response.data;
            }
            
            throw new Error(response.data?.message || 'Failed to fetch video statistics');
        } catch (error) {
            console.error('Error fetching video statistics:', error);
            throw this.handleError(error);
        }
    }

    // Get comprehensive admin analytics with better error handling
    async getAnalytics(timeRange = '30d', adminEmail) {
        try {
            console.log(`Fetching admin analytics - timeRange: ${timeRange}, adminEmail: ${adminEmail || 'not provided'}`);
            
            const response = await this.apiClient.get('/analytics/get', {
                params: {
                    timeRange,
                    includeDetails: 'true'
                },
                headers: {
                    'x-admin-email': adminEmail || 'development@theoilsbabe.com'
                }
            });

            console.log('Admin analytics response:', response.data);

            if (response.data && response.data.success && response.data.data) {
                return response.data.data;
            } else if (response.data && response.data.data) {
                // Handle legacy response format
                return response.data.data;
            } else {
                throw new Error(response.data?.message || 'Failed to retrieve analytics');
            }
        } catch (error) {
            console.error('Admin API Error:', error);
            
            // Provide fallback data if API fails
            if (error.response?.status === 404) {
                console.warn('Admin analytics endpoint not found, providing fallback data');
                return this.getFallbackAnalyticsData(timeRange);
            }
            
            throw error;
        }
    }

    // Provide fallback analytics data when API is unavailable
    getFallbackAnalyticsData(timeRange) {
        return {
            overview: {
                totalUsers: 150,
                activeUsers: 45,
                totalVideos: 25,
                totalViews: 320,
                totalWatchTime: 1200, // minutes
                certificatesEarned: 15,
                newFavorites: 78,
                engagementRate: 30,
                averageWatchTime: 4
            },
            userDistribution: {
                byRank: {
                    basic: 120,
                    premier: 20,
                    silver: 7,
                    gold: 3
                },
                byCountry: {
                    'Romania': 100,
                    'Unknown': 30,
                    'Moldova': 15,
                    'Italia': 5
                }
            },
            popularContent: {
                topVideos: [
                    { videoId: 1, views: 45 },
                    { videoId: 2, views: 32 },
                    { videoId: 3, views: 28 }
                ]
            },
            timeRange,
            period: {
                from: new Date(Date.now() - (30 * 24 * 60 * 60 * 1000)).toISOString(),
                to: new Date().toISOString(),
                days: 30
            },
            generatedAt: new Date().toISOString(),
            metadata: {
                version: "2.0-fallback",
                apiEndpoint: "adminAnalyticsGET-fallback",
                note: "This is fallback data"
            }
        };
    }

    // User Management Functions
    async getUsers(filters = {}) {
        try {
            const { page = 1, limit = 20, search, rank, status, adminEmail } = filters;
            
            const response = await this.apiClient.get('/adminUserManagement', {
                params: {
                    page,
                    limit,
                    search,
                    rank,
                    status,
                    adminEmail
                }
            });

            if (response.data.success) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || 'Failed to retrieve users');
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            throw error;
        }
    }

    async createUser(userData, adminEmail) {
        try {
            const response = await this.apiClient.post('/adminUserManagement', {
                ...userData,
                adminEmail
            });

            if (response.data.success) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || 'Failed to create user');
            }
        } catch (error) {
            console.error('Error creating user:', error);
            throw error;
        }
    }

    async updateUser(userId, updateData, adminEmail) {
        try {
            const response = await this.apiClient.put('/adminUserManagement', {
                ...updateData,
                adminEmail
            }, {
                params: { userId }
            });

            if (response.data.success) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || 'Failed to update user');
            }
        } catch (error) {
            console.error('Error updating user:', error);
            throw error;
        }
    }

    async deleteUser(userId, adminEmail) {
        try {
            const response = await this.apiClient.delete('/adminUserManagement', {
                params: { userId },
                data: { adminEmail }
            });

            if (response.data.success) {
                return response.data;
            } else {
                throw new Error(response.data.message || 'Failed to delete user');
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            throw error;
        }
    }

    // Certificate Management Functions
    async getCertificates(filters = {}) {
        // Mock implementation - replace with actual API call when backend is ready
        return {
            certificates: [
                {
                    id: 'cert_001',
                    userName: 'Maria Popescu',
                    userEmail: 'maria@example.com',
                    certificateName: 'Essential Oils Basics',
                    score: 91.5,
                    issuedAt: '2025-01-15T10:30:00Z',
                    status: 'issued',
                    category: 'Beginner'
                },
                {
                    id: 'cert_002',
                    userName: 'Alexandru Ion',
                    userEmail: 'alex@example.com',
                    certificateName: 'Advanced Aromatherapy',
                    score: 87.2,
                    issuedAt: '2025-01-14T15:45:00Z',
                    status: 'pending',
                    category: 'Advanced'
                }
            ],
            pagination: {
                page: 1,
                limit: 20,
                total: 156,
                pages: 8
            }
        };
    }

    async approveCertificate(certificateId, adminEmail) {
        // Mock implementation - replace with actual API call when backend is ready
        return {
            success: true,
            message: 'Certificate approved successfully'
        };
    }

    async rejectCertificate(certificateId, reason, adminEmail) {
        // Mock implementation - replace with actual API call when backend is ready
        return {
            success: true,
            message: 'Certificate rejected successfully'
        };
    }

    // Activity Tracking Functions
    async trackActivity(activityData) {
        try {
            // Mock implementation - replace with actual API call when backend is ready
            console.log('Tracking activity:', activityData);
            return {
                success: true,
                message: 'Activity tracked successfully'
            };
        } catch (error) {
            console.error('Error tracking activity:', error);
            throw error;
        }
    }

    async getUserActivity(userId, timeRange = '30d') {
        // Mock implementation - replace with actual API call when backend is ready
        return {
            activities: [
                {
                    id: 'activity_001',
                    type: 'video_completed',
                    timestamp: '2025-01-20T14:30:00Z',
                    details: {
                        videoId: 'video_123',
                        videoTitle: 'Essential Oils Introduction',
                        duration: 1800
                    }
                },
                {
                    id: 'activity_002',
                    type: 'certificate_earned',
                    timestamp: '2025-01-19T16:45:00Z',
                    details: {
                        certificateId: 'cert_456',
                        certificateName: 'Safety Guidelines',
                        score: 94.2
                    }
                }
            ],
            summary: {
                totalActivities: 45,
                videosWatched: 28,
                certificatesEarned: 3,
                totalWatchTime: 12650
            }
        };
    }

    // Utility Functions
    isAdmin(userEmail) {
        const adminEmails = [
            process.env.REACT_APP_ADMIN_EMAIL,
            process.env.REACT_APP_ARI_ADMIN_EMAIL,
            'ari@iserenify.com',
            'admin@theoilsbabe.com'
        ].filter(Boolean);
        
        return adminEmails.includes(userEmail?.toLowerCase());
    }

    formatAnalyticsData(rawData) {
        if (!rawData) return null;

        return {
            ...rawData,
            overview: {
                ...rawData.overview,
                growthRate: this.calculateGrowthRate(rawData.users?.registrationTrend || []),
                engagementTrend: this.calculateEngagementTrend(rawData.engagement?.dailyActiveUsers || [])
            }
        };
    }

    calculateGrowthRate(registrationTrend) {
        if (!registrationTrend || registrationTrend.length < 2) return 0;
        
        const recent = registrationTrend.slice(0, 7).reduce((sum, day) => sum + (day.registrations || 0), 0);
        const previous = registrationTrend.slice(7, 14).reduce((sum, day) => sum + (day.registrations || 0), 0);
        
        if (previous === 0) return recent > 0 ? 100 : 0;
        return Math.round(((recent - previous) / previous) * 100);
    }

    calculateEngagementTrend(dailyActiveUsers) {
        if (!dailyActiveUsers || dailyActiveUsers.length < 2) return 0;
        
        const recent = dailyActiveUsers.slice(0, 7).reduce((sum, day) => sum + (day.users || 0), 0) / 7;
        const previous = dailyActiveUsers.slice(7, 14).reduce((sum, day) => sum + (day.users || 0), 0) / 7;
        
        if (previous === 0) return recent > 0 ? 100 : 0;
        return Math.round(((recent - previous) / previous) * 100);
    }

    exportAnalytics(analyticsData, format = 'json') {
        try {
            const timestamp = new Date().toISOString().split('T')[0];
            const filename = `theoilsbabe-analytics-${timestamp}`;
            
            if (format === 'csv') {
                return this.exportToCSV(analyticsData, filename);
            } else {
                return this.exportToJSON(analyticsData, filename);
            }
        } catch (error) {
            console.error('Error exporting analytics:', error);
            throw error;
        }
    }

    exportToJSON(data, filename) {
        const jsonData = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonData], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        return true;
    }

    exportToCSV(data, filename) {
        // Simplified CSV export for overview data
        const csvData = [
            ['Metric', 'Value'],
            ['Total Users', data.overview?.totalUsers || 0],
            ['Active Users', data.overview?.activeUsers || 0],
            ['New Users', data.overview?.newUsers || 0],
            ['Total Videos', data.overview?.totalVideos || 0],
            ['Total Watch Time', data.overview?.totalWatchTime || 0],
            ['Certificates Issued', data.overview?.certificatesIssued || 0],
            ['Average Engagement', data.overview?.averageEngagement || 0]
        ];
        
        const csvContent = csvData.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = `${filename}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        return true;
    }

    // Handle API errors consistently
    handleError(error) {
        if (error.response) {
            // Server responded with error status
            const message = error.response.data?.message || error.response.data || 'Server error';
            return new Error(`API Error: ${message}`);
        } else if (error.request) {
            // Request was made but no response received
            return new Error('Network error: No response from server');
        } else {
            // Something else happened
            return new Error(`Request error: ${error.message}`);
        }
    }
}

const adminService = new AdminService();
export default adminService;
